import React from 'react';
import PropTypes from 'prop-types';
import { MdSearch, MdClose, MdFilterAltOff } from 'react-icons/md';
import { Button, ButtonGroup, ReportDateRangePicker } from 'components/common';

const SCOPES = [
  // Named for what it now lists. It used to say "Active only" while showing
  // every session that had not been signed out — including people who had shut
  // their laptop hours earlier — so the green/grey dot contradicted the tab.
  { key: 'active', label: 'Online now' },
  { key: 'all', label: 'All sessions' },
  // The triage list an alert links to. Kept as a scope rather than a checkbox so
  // it survives in the URL and can be sent to somebody.
  { key: 'suspicious', label: 'Suspicious' },
];

const NETWORKS = [
  { value: '', label: 'Any network' },
  { value: 'internal', label: 'Internal (office LAN)' },
  { value: 'external', label: 'External (internet / VPN)' },
];

const selectClass =
  'tw-h-8 tw-px-2 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-text-slate-600 focus:tw-outline-none focus:tw-border-primary-400';

/**
 * Filters for the session list.
 *
 */
const SessionFilterBar = ({
  scope, onScopeChange,
  query, onQueryChange,
  provider, onProviderChange, providerOptions,
  network, onNetworkChange,
  startDate, endDate, onDateChange,
  onReset, hasFilters,
}) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-mb-2">
    <ButtonGroup aria-label="Session scope">
      {SCOPES.map((option) => (
        <Button
          key={option.key}
          size="sm"
          variant={scope === option.key ? 'primary' : 'ghost'}
          onClick={() => onScopeChange(option.key)}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>

    <div className="tw-relative tw-flex tw-items-center">
      <MdSearch size={15} className="tw-absolute tw-left-2.5 tw-text-slate-400 tw-pointer-events-none" />
      <input
        type="text"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Name, email, IP…"
        className="tw-h-8 tw-pl-8 tw-pr-7 tw-w-56 tw-text-sm tw-rounded-md tw-border tw-border-solid tw-border-slate-200 focus:tw-border-primary-400 focus:tw-outline-none"
      />
      {query && (
        <button
          type="button"
          onClick={() => onQueryChange('')}
          title="Clear search"
          className="tw-absolute tw-right-1.5 tw-text-slate-400 hover:tw-text-slate-700 tw-bg-transparent tw-border-0 tw-p-0.5"
        >
          <MdClose size={14} />
        </button>
      )}
    </div>

    <select value={provider} onChange={(e) => onProviderChange(e.target.value)} className={selectClass}>
      <option value="">Any sign-in method</option>
      {providerOptions.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>

    <select value={network} onChange={(e) => onNetworkChange(e.target.value)} className={selectClass}>
      {NETWORKS.map((option) => (
        <option key={option.value || 'any'} value={option.value}>{option.label}</option>
      ))}
    </select>

    {/* On every tab. On Online now it narrows by when people were last seen,
        on the others by when they signed in — see the controller for why the
        two tabs measure different timestamps. */}
    <ReportDateRangePicker
      startDate={startDate}
      endDate={endDate}
      onChange={onDateChange}
    />

    {hasFilters && (
      <button
        type="button"
        onClick={onReset}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-2.5 tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-xs tw-font-semibold tw-text-slate-500 hover:tw-bg-slate-50 hover:tw-text-primary-600 tw-transition-colors"
      >
        <MdFilterAltOff size={14} /> Clear
      </button>
    )}
  </div>
);

SessionFilterBar.propTypes = {
  scope: PropTypes.string.isRequired,
  onScopeChange: PropTypes.func.isRequired,
  query: PropTypes.string.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  provider: PropTypes.string.isRequired,
  onProviderChange: PropTypes.func.isRequired,
  providerOptions: PropTypes.array.isRequired,
  network: PropTypes.string.isRequired,
  onNetworkChange: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onDateChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  hasFilters: PropTypes.bool,
};

export default SessionFilterBar;
